import { Facet, FacetDetail } from "@/types/productListing";
import { TertiaryButton as V31TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { TertiaryButton as DeprecatedTertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { ChevronDownOutlined as ChevronDown24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronDownOutlined";
import { ChevronUpOutlined as ChevronUp24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronUpOutlined";
import styles from "./FilterButtonWithDropdown.module.scss";
import classNames from "classnames/bind";
import { FilterDropdown } from "../FilterDropdown/FilterDropdown";
import { useRef, useEffect } from "react";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";
import { useFeatureFlags } from "@/flags/flags";

const cx = classNames.bind(styles);

const ChevronDownSmallIcon = () => <ChevronDown24PxOutlinedIcon size="small" />;
const ChevronUpSmallIcon = () => <ChevronUp24PxOutlinedIcon size="small" />;

type FilterButtonWithDropdownProps = {
  dispatchFacetAnalytics: (
    eventType: string,
    facetName: string,
    facetDetails: FacetDetail,
  ) => void;
  facet: Facet;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
};

export function FilterButtonWithDropdown({
  dispatchFacetAnalytics,
  facet,
  isOpen,
  onToggle,
  onClose,
}: FilterButtonWithDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { isRequestAnimationFrameOn, isJldsV31Enabled } = useFeatureFlags();

  const handleToggle = () => {
    if (isRequestAnimationFrameOn?.value) {
      requestAnimationFrame(() => {
        onToggle();
      });
    } else {
      onToggle();
    }
  };

  // Handle click outside
  useEffect(
    function subscribeToOutsideCloseInteractions() {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      // Handle ESC key
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);

      return function cleanupOutsideCloseHandlers() {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscKey);
      };
    },
    [isOpen, onClose],
  );

  return (
    <div
      ref={containerRef}
      className={cx("filterButtonWithDropdown")}
      data-testid={`filter-button-container-${facet.dimensionName}`}
    >
      {isJldsV31Enabled?.value ? (
        <V31TertiaryButton
          className={cx("filterButton", {
            "filterButton--open": isOpen,
          })}
          data-testid={`filter-button-${facet.dimensionName}`}
          onClick={() => {
            sendMonetateEvent("plp.facets.interaction");
            sendMonetateEvent("plp.facets.interaction.top5");
            handleToggle();
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
          variant="white"
          labelText={facet.name}
          icon={isOpen ? ChevronUpSmallIcon : ChevronDownSmallIcon}
        />
      ) : (
        <DeprecatedTertiaryButton
          className={cx("filterButton", {
            "filterButton--open": isOpen,
          })}
          data-testid={`filter-button-${facet.dimensionName}`}
          onClick={() => {
            sendMonetateEvent("plp.facets.interaction");
            sendMonetateEvent("plp.facets.interaction.top5");
            handleToggle();
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
          small
          variant="clear-dark"
        >
          <span className={styles.buttonContent}>
            <span className={styles.facetName}>{facet.name}</span>
            <span className={styles.chevronIcon}>
              {isOpen ? (
                <ChevronUp24PxOutlinedIcon size="small" />
              ) : (
                <ChevronDown24PxOutlinedIcon size="small" />
              )}
            </span>
          </span>
        </DeprecatedTertiaryButton>
      )}
      {isOpen && (
        <FilterDropdown
          facet={facet}
          isOpen={isOpen}
          dispatchFacetAnalytics={dispatchFacetAnalytics}
        />
      )}
    </div>
  );
}
